<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import type { Service } from "@/entities/service";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui";

import { useServicesCatalogStore } from "../model/store";

/**
 * Діалог видалення послуги (spec §7.5, 187-T6, ADR-0015, ADR-0023).
 * Cancel focused by default (ADR-0015).
 * 409 service_in_use → blocked state, confirm disabled.
 */
const props = defineProps<{
  open: boolean;
  service: Service;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  deleted: [];
}>();

const { t } = useI18n();
const store = useServicesCatalogStore();

type DeleteStatus = "idle" | "removing" | "blocked";
const deleteStatus = ref<DeleteStatus>("idle");

async function onConfirm(): Promise<void> {
  if (deleteStatus.value === "removing") return;
  deleteStatus.value = "removing";

  await store.confirmDelete(props.service.id);

  // Check outcome by inspecting row state post-await
  const row = store.rows.find(
    (r) => r.kind === "saved" && r.service.id === props.service.id,
  );

  if (!row) {
    // 204 or 404 — row gone → success
    deleteStatus.value = "idle";
    emit("deleted");
    emit("update:open", false);
  } else if (row.kind === "saved" && row.mode === "blocked") {
    // 409 service_in_use
    deleteStatus.value = "blocked";
  } else {
    // Other error: toast was shown by store, row reverted to deleteConfirm
    deleteStatus.value = "idle";
  }
}

function onCancel(): void {
  store.cancelDelete(props.service.id);
  deleteStatus.value = "idle";
  emit("update:open", false);
}
</script>

<template>
  <AlertDialog :open="open" @update:open="(v) => { if (!v) onCancel(); }">
    <AlertDialogContent data-testid="delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ t("services.delete.confirm", { name: service.name }) }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <!-- Base text: always visible -->
          <span data-testid="delete-base-text">
            {{ t("services.delete.base") }}
          </span>
          <!-- Assigned-masters consequence warning (count > 0) -->
          <span
            v-if="service.assignedMasterCount > 0"
            data-testid="delete-assigned-warning"
          >
            {{
              t(
                "services.delete.assignedWarning",
                { count: service.assignedMasterCount },
                service.assignedMasterCount,
              )
            }}
          </span>
          <!-- Blocked by active bookings (409) -->
          <span
            v-if="deleteStatus === 'blocked'"
            data-testid="delete-blocked-message"
          >
            {{ t("services.delete.blocked") }}
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <!-- ADR-0015: Cancel focused by default (destructive safety) -->
        <AlertDialogCancel
          data-testid="delete-cancel"
          @click="onCancel"
        >
          {{ t("services.actions.cancel") }}
        </AlertDialogCancel>
        <AlertDialogAction
          variant="destructive"
          :disabled="deleteStatus === 'blocked' || deleteStatus === 'removing'"
          data-testid="delete-confirm"
          @click.prevent="onConfirm"
        >
          {{ deleteStatus === "removing" ? t("services.saving") : t("services.actions.delete") }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
